import React, { useState, useEffect } from 'react';
import { GearDisplay } from '../components/gearDisplay';
import { helmetHiddenPowerData, chestplateHiddenPowerData, leggingsHiddenPowerData, weaponHiddenPowerData } from '../data/hiddenPowerData';
import '../css/equipment.css'

//Default gear before selection
const defaultGear =  {
      id: 0,
      type: null,
      rarity: "None",
      name: "Click to Select",
      set: "None",
      level: 60,
      maxLevel: 60,
      stars: 5,
      baseStats: [
        { key: "None", value: 1, perLevel: 0 }
      ],
      uniqueStats: [
        { key: "None", value: 0, perStar: 0}
      ],
    };

  const attuneable = ["Epic", "Super Epic", "Unique", "Event"]
    
  //add type to default gear
  const makeDefaultGear = (type) => ({
    ...defaultGear,
    type
  });

const EquipmentWrapper = ({ setGearStats, context }) => {
  const [weapon, setWeapon] = useState(makeDefaultGear("Weapon"));
  const [helmet, setHelmet] = useState(makeDefaultGear("Helmet"));
  const [chestplate, setChestplate] = useState(makeDefaultGear("Chestplate"));
  const [leggings, setLeggings] = useState(makeDefaultGear("Leggings"));

  const [weaponHP, setWeaponHP] = useState([null, null]);
  const [helmetHP, setHelmetHP] = useState([null, null]);
  const [chestplateHP, setChestplateHP] = useState([null, null]);
  const [leggingsHP, setLeggingsHP] = useState([null, null]);
  //Attunement
  const [isAttuned, setIsAttuned] = useState(false);



  // Set bonus
  const checkSetBonus = () => {
    const pieces = [helmet, chestplate, leggings];
    const counts = {};

    pieces.forEach(piece => {
      if (!piece?.setKey) return;
      counts[piece.setKey] = (counts[piece.setKey] || 0) + 1;
    });

    const activeSets = {};
    for (const [setName, count] of Object.entries(counts)) {
      if (count >= 3) activeSets[setName] = true;
    }
    return activeSets;
  };

  const activeSets = checkSetBonus();

  // Hidden power
  const updateHiddenPower = (setter, index, value) => {
    setter(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  // Gather all gear stats and pass it to characterStats
  useEffect(() => {
    const computeTotalGearStats = () => {
      const gearPieces = [
        { piece: weapon, hidden: weaponHP },
        { piece: helmet, hidden: helmetHP },
        { piece: chestplate, hidden: chestplateHP },
        { piece: leggings, hidden: leggingsHP },
      ];
    
      const allStats = [];
      const allBuffs = [];
    
      gearPieces.forEach(({ piece, hidden }) => {
        if (piece.baseStats) {
          piece.baseStats.forEach(stat => {
            allStats.push({
              key: stat.key,
              type: `${piece.type} Base Stat`,
              value: stat.value,
              target: stat.target,
            });
          });
        }
    
        if (piece.uniqueStats) {
          piece.uniqueStats.forEach(stat => {
            //Since stats don't support targets for atk type, attack type related stats are delegated to 'hidden' buffs
            if (stat.target && stat.target.type) {
              allBuffs.push({
                name: `${piece.type} Unique Stat`,
                type: `Armor Piece Bonus`,
                hidden: "true",
                stats: [{
                  key: stat.key,
                  type: `${piece.type} Unique Stat`,
                  value: stat.value,
                  target: stat.target
              }]
              });

            }
            else {
              allStats.push({
                key: stat.key,
                type: `${piece.type} Unique Stat`,
                value: stat.value,
                target: stat.target,
              });
          }
          });
        }
        
        // Hidden powers don't have targets
        hidden.forEach(hp => {
          if (hp) {
            allStats.push({
              key: hp[1],
              type: `${piece.type} Hidden Power`,
              value: hp[2],
            });
          }
        });
      });

      let attacks = {}
      //Set Bonus is applied not per piece
      if (activeSets[helmet.setKey] === true) {
        helmet?.setBonus?.stats?.forEach(stat => {
          allStats.push({
            key: stat.key,
            type: 'Set Bonus',
            value: stat.value,
            target: stat.target,
          });
        });
        
        helmet?.setBonus?.buffs?.forEach(buff => {
          allBuffs.push(buff);
        });
        if (helmet?.setBonus?.attacks) {
          attacks = helmet.setBonus.attacks
        }
      }
      //Hidden power attunement adds additional stats regardless of gear type
      if (isAttuned) {
        allStats.push ({
          key: 'ElementalATK',
          type: 'Attunement',
          value: 20
        })
        allStats.push ({
          key: 'ElementalDMG',
          type: 'Attunement',
          value: 30
        })
      }
      
      return {stats: allStats, buffs: allBuffs, attacks: attacks};
    };

    const result = computeTotalGearStats();
    setGearStats(result);
    
  }, [
    weapon, helmet, chestplate, leggings,
    weaponHP, helmetHP, chestplateHP, leggingsHP, isAttuned
  ]);

  return (
    
    <div className="equipmentWrapper basicWrapper">
      <h2 className='sectionTitle'>Equipment</h2>
      <div className="gearWrapper">
        <GearDisplay
          type="Weapon"
          piece={weapon}
          hiddenPowers={weaponHP}
          onPieceChange={setWeapon}
          onHiddenPowerChange={(index, value) => updateHiddenPower(setWeaponHP, index, value)}
          hiddenPowerData={weaponHiddenPowerData}
          context={context}
        />
        <GearDisplay
          type="Helmet"
          piece={helmet}
          hiddenPowers={helmetHP}
          onPieceChange={setHelmet}
          onHiddenPowerChange={(index, value) => updateHiddenPower(setHelmetHP, index, value)}
          hiddenPowerData={helmetHiddenPowerData}
          isSetActive={!!activeSets[helmet?.setKey]}
          context={context}
        />
        <GearDisplay
          type="Chestplate"
          piece={chestplate}
          hiddenPowers={chestplateHP}
          onPieceChange={setChestplate}
          onHiddenPowerChange={(index, value) => updateHiddenPower(setChestplateHP, index, value)}
          hiddenPowerData={chestplateHiddenPowerData}
          isSetActive={!!activeSets[chestplate?.setKey]}
          context={context}
        />
        <GearDisplay
          type="Leggings"
          piece={leggings}
          hiddenPowers={leggingsHP}
          onPieceChange={setLeggings}
          onHiddenPowerChange={(index, value) => updateHiddenPower(setLeggingsHP, index, value)}
          hiddenPowerData={leggingsHiddenPowerData}
          isSetActive={!!activeSets[leggings?.setKey]}
          context={context}
        />

      </div>
      <div className='attunementWrapper'>
          <label className="attunementToggle">
            <input
              id = "attunement"
              type="checkbox"
              checked={isAttuned}
              src={`./img/Icons/Attunement/${context.element}`}
              onChange={(e) => setIsAttuned(e.target.checked)}
              style={{ display: 'none' }}
            />
            <img
              src={`./img/Icons/Attunement/${context.element}.webp`}
              alt="Attunement"
              className={`attuneImage ${isAttuned ? 'attuned' : 'unattuned'}`}
            />
            Elemental Scrolls Used
            <p className={`attuneText ${isAttuned ? 'attuned' : 'unattuned'}`}>
              (+20 Elemental Attack, +30% Elemental Damage)
            </p>
          </label>
      </div>
    </div>
    
    
    
  );
};

export default EquipmentWrapper;
